import React, { useState, useEffect, useCallback } from 'react'; // Import useCallback
import { FaEllipsisVertical } from 'react-icons/fa6';
import { FaList, FaTh, FaPlus, FaSearch } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router';
// import Table from '../../components/Table';
import ApiService from '../../services/ApiService';
import { initialAuthState } from '../../services/ApiService';
import { getPermissions } from '../../common/commonUtils';
import { formatString } from '../../common/commonUtils';

const Staff = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const employeeData = location.state?.staffDetails || {};
  const role = localStorage.getItem("role");

  const [selectedBranch, setSelectedBranch] = useState('');
  const [branches, setBranches] = useState([]);
  const [staffId, setStaffId] = useState('');
  const [staffName, setStaffName] = useState('');
  const [isGridView, setIsGridView] = useState(true);
  const [menuOpenIndex, setMenuOpenIndex] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [columns, setColumns] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [isSearch, setIsSearch] = useState(false);

  const columnNames = [
    'ID',
    'Staff ID',
    'Staff Name',
    'Designation',
    'Phone Number',
    'Email',
    'Branch Name',
    'address',
    'status'
  ];

  // Fetch Staff Details using useCallback to memoize the function
  const getStaffSearchDetails = useCallback(async () => {
    try {
      const response = await ApiService.post(
        '/dashboards/getStaffSearchDetails',
        {
          // staffId: staffId,
          branchName: selectedBranch,
          staffName: staffName,
          companyCode: initialAuthState?.companyCode,
          unitCode: initialAuthState?.unitCode,
        }
      );

      if (response.status) {
        const rawData = response.data || [];

        // Define a mapping from your API response keys to the column names
        const columnMapping = {
          id: 'ID',
          staffId: 'Staff ID',
          staffName: 'Staff Name',
          designation: 'Designation',
          phoneNumber: 'Phone Number',
          email: 'Email',
          branch_name: 'Branch Name',
          address: 'address',
          status: 'status'
        };

        // Reorder the columns based on `columnNames`
        const formattedData = rawData.map((item) => {
          let reorderedItem = {};
          columnNames.forEach((column) => {
            const key = Object.keys(columnMapping).find(
              (k) => columnMapping[k] === column
            );
            if (key && item.hasOwnProperty(key)) {
              reorderedItem[column] = item[key];
            }
          });
          return reorderedItem;
        });

        setProfiles(formattedData);
        setColumns(columnNames);
      } else {
        alert(
          response.data.internalMessage || 'Failed to fetch staff details.'
        );
      }
    } catch (error) {
      console.error('Error fetching staff details:', error);
      alert('Failed to fetch staff details.');
    }
  }, [staffId, selectedBranch, staffName]);

  // Fetch Branch Names
  const fetchBranches = async () => {
    try {
      const response = await ApiService.post('/branch/getBranchNamesDropDown');
      if (response.status) {
        setBranches(response.data);
      } else {
        console.error('Failed to fetch branch names.');
      }
    } catch (error) {
      console.error('Error fetching branch names:', error);
    }
  };

  const handleSearch = async () => {
    let filteredProfiles = profiles;

    if (staffId.trim() !== '') {
      filteredProfiles = filteredProfiles.filter((profile) =>
        profile['Staff ID']?.toLowerCase().includes(staffId.toLowerCase())
      );
    }

    if (staffName.trim() !== '') {
      filteredProfiles = filteredProfiles.filter((profile) =>
        profile['Staff Name']?.toLowerCase().includes(staffName.toLowerCase())
      );
    }
    setIsSearch(true);
    setProfiles(filteredProfiles);
  };

  // Initial API calls
  useEffect(() => {
    const perms = getPermissions('staff');
    setPermissions(perms);
    getStaffSearchDetails();
    fetchBranches();
  }, [getStaffSearchDetails]); // Include getStaffSearchDetails in the dependency array

  useEffect(() => {
    fetchBranches();
  }, []);
  // Handle branch selection
  const handleSelectChange = (e) => {
    setSelectedBranch(e.target.value);
  };

  // Toggle dropdown menu
  const toggleMenu = (index) => {
    setMenuOpenIndex(menuOpenIndex === index ? null : index);
  };

  // Navigate to edit page
  const handleEdit = (profile) => {
    navigate('/edit-staff', { state: { staffDetails: profile } });
  };

  // Navigate to details page
  const handleMoreDetails = (profile) => {
    navigate('/staff-details', { state: { staffDetails: profile } });
  };

  const handleDelete = async (row) => {
    const id = row.ID;
    const confirmDelete = window.confirm(
      'Are you sure you want to delete this record?'
    );
    if (!confirmDelete) return;

    try {
      const response = await ApiService.post('/staff/handleStaffDetails', {
        id: row.ID,
        staffStatus: 'INACTIVE',
        companyCode: initialAuthState.companyCode,
        unitCode: initialAuthState.unitCode,
      });

      if (response.status) {
        alert('Staff deleted successfully');
        getStaffSearchDetails();
        // Update profiles state to remove the deleted staff
        setProfiles((prevProfiles) =>
          prevProfiles.filter((staff) => staff.id !== id)
        );
      } else {
        alert('Failed to delete staff');
      }
    } catch (error) {
      console.error('Error deleting staff:', error);
      alert('An error occurred while deleting the staff.');
    }
  };

  return (
    <div className="m-2">
      {/* Header */}
      <div className="flex justify-between items-center py-4">
        <h2 className="text-2xl font-semibold text-gray-800">Staff Details</h2>
        <div className="flex items-center space-x-4">
          <button
            className={`flex items-center space-x-2 text-white px-4 py-2 rounded-md cursor-pointer ${permissions.add ? 'bg-green-700' : 'bg-gray-400 cursor-not-allowed opacity-50'}`}
            onClick={() => navigate('/add-staff')}
            disabled={!permissions.add}
          >
            <FaPlus size={16} />
            <span>Add Staff</span>
          </button>
        </div>
      </div>
      <div className="flex space-x-4 my-4">
        <input
          placeholder="Staff ID"
          onChange={(e) => setStaffId(e.target.value)}
          id="staffId"
          value={staffId}
          className="h-12 w-full block px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-blue-500"
        />
        <input
          placeholder="Staff Name"
          onChange={(e) => setStaffName(e.target.value)}
          id="staffName"
          value={staffName}
          className="h-12 w-full block px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-blue-500"
        />
        <select
          id="branchDropdown"
          value={selectedBranch}
          onChange={handleSelectChange}
          className="h-12 w-full block px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md focus:outline-none"
        >
          {branches.map((branch) => (
            <option key={branch.branchName} value={branch.branchName}>
              {branch.branchName}
            </option>
          ))}
        </select>
        <button
          onClick={handleSearch}
          className="h-12 w-full bg-green-700 text-white px-4 py-2 rounded-md transition duration-200 hover:bg-green-800 focus:outline-none focus:ring focus:ring-green-500"
        >
          {/* <FaSearch className="mr-2" /> */}
          Search
        </button>
      </div>
      {/* Staff Table */}
      {isSearch && <Table
        columns={columns}
        columnNames={columnNames}
        onDelete={handleDelete}
        onEdit={handleEdit}
        showDelete={permissions.delete}
        showEdit={permissions.edit}
        showDetails={permissions.edit}
        onDetails={handleMoreDetails}

        data={
          Array.isArray(profiles)
            ? [...profiles].sort((a, b) =>
              String(a['Staff ID']).localeCompare(String(b['Staff ID']))
            )
            : []
        }
      />}
      {/* )}{' '} */}
    </div>
  );
};

export default Staff;

const getStatusStyle = (status) => {
  switch (status) {
    case 'Paid':
    case 'Present':
    case 'Accepted':
    case 'Approved':
    case 'Success':
      return {
        textColor: 'text-green-600',
        backgroundColor: 'bg-green-100',
      };
    case 'FAILED':
    case 'Absent':
    case 'Declined':
    case 'Unpaid':
    case 'Failed':
      return {
        textColor: 'text-red-600',
        backgroundColor: 'bg-red-100',
      };
    case 'IN PROGRESS':
    case 'Off Leave':
    case 'Sent':
    case 'Overdue':
    case 'Pending':
    case 'pending':
    case 'Processing':
      return {
        textColor: 'text-orange-600',
        backgroundColor: 'bg-orange-100',
      };
    default:
      return {
        textColor: 'text-gray-600',
        backgroundColor: 'bg-gray-100',
      };
  }
};

const checkColumn = (col) => {
  return (
    col === 'Status' ||
    col === 'status' ||
    col === 'attendance' ||
    col === 'paymentStatus'
  );
};

const Table = ({
  name,
  columns = [],
  columnNames = [],
  data = [],
  onEdit,
  onDetails,
  onDelete,
  showEdit = false,
  showDelete = true,
  showDetails = true,
  editText = 'Edit',
  deleteText = 'Delete',
  detailsText = 'More Details',
}) => {
  const [openRowIndex, setOpenRowIndex] = useState(null);
  const handleActionClick = (index) => {
    setOpenRowIndex(openRowIndex === index ? null : index);
  };
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [showRowPopup, setShowRowPopup] = useState(false);
  return (
    <div className="overflow-hidden rounded-lg shadow">
      {columns.length === 0 || data.length === 0 ? (
        <div className="p-4 text-center text-gray-500">No data found</div>
      ) : (
        <div className="overflow-y-auto" style={{maxHeight: '500px', maxWidth: '100%'}}>
          <table className="min-w-full border-collapse border border-gray-200">
            <thead className="bg-gray-100 sticky top-0">
              <tr className="bg-blue-500 text-white text-left">
                {columnNames.map((column, index) => {
                  const minWidth = Math.max(column.length * 10, 120); // Ensure wider space for longer names
                  return (
                    <th
                      key={index}
                      className="px-4 py-3 border capitalize whitespace-nowrap"
                      style={{
                        textTransform: 'capitalize',
                        minWidth: `${minWidth}px`,
                      }}
                    >
                      {formatString(column)}
                    </th>
                  );
                })}
                <th className="px-4 py-3 border capitalize whitespace-nowrap min-w-[120px]">
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {data.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className={`${row.status === "INACTIVE"
                      ? "bg-red-50" // 👈 highlight inactive rows (change color as you like)
                      : rowIndex % 2 === 0
                        ? "bg-white"
                        : "bg-gray-50"
                    }`}
                // onClick={() => {
                //   setSelectedRowData(row);
                //   setShowRowPopup(true);
                // }}
                >
                  {columns.map((column, colIndex) => (
                    <td
                      key={colIndex}
                      className={`border-b border-gray-300 px-4 py-2 text-sm text-gray-600 ${checkColumn(column)
                        ? `${getStatusStyle(row[column]).textColor} ${getStatusStyle(row[column]).backgroundColor}`
                        : ''
                        } ${column === 'Staff ID' ? 'cursor-pointer hover:underline text-blue-600 hover:bg-blue-500 hover:text-white' : ''}`}
                      onClick={
                        column === 'Staff ID'
                          ? () => {
                            setSelectedRowData(row);
                            setShowRowPopup(true);
                          }
                          : undefined
                      }
                    >
                      {row[column] || '-'}
                    </td>
                  ))}
                  <td className="border-b border-gray-300 px-4 py-2 text-sm text-gray-600 relative">
                    <span
                      onClick={() => handleActionClick(rowIndex)}
                      className="cursor-pointer"
                    >
                      <FaEllipsisVertical />
                    </span>
                    {openRowIndex === rowIndex && (
                      <div className="absolute top-15 right-0 w-32 bg-white border rounded shadow-lg z-10">
                        {/* {showEdit && (
                          <button
                            onClick={() => {
                              onEdit(row);
                              setOpenRowIndex(null);
                            }}
                            className="block w-full px-4 py-2 text-left text-sm hover:bg-gray-100"
                          >
                            {editText}
                          </button>
                        )} */}
                        {showDelete && (
                          <button
                            onClick={() => {
                              onDelete(row);
                              setOpenRowIndex(null);
                            }}
                            className="block w-full px-4 py-2 text-left text-sm hover:bg-gray-100"
                          >
                            {deleteText}
                          </button>
                        )}
                        {showDetails && (
                          <button
                            onClick={() => {
                              onDetails(row);
                              setOpenRowIndex(null);
                            }}
                            className="block w-full px-4 py-2 text-left text-sm hover:bg-gray-100"
                          >
                            {detailsText}
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showRowPopup && selectedRowData && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-96 p-4 relative">
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-800"
              onClick={() => setShowRowPopup(false)}
            >
              ×
            </button>
            <h2 className="text-lg font-semibold mb-2">Staff Details</h2>
            <div className="space-y-2">
              {columns.map((column, index) => (
                <div key={index} className="flex justify-between text-sm">
                  <span className="font-medium">{column}:</span>
                  <span>{selectedRowData[column] || '-'}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
